import {
    ActualOnDatePriceQuery,
    ListOfPricesActualOnDateResponse,
    PriceQuery,
    PurchasePricesQuery,
    PurchasePriceData,
    PricesResponse,
    PriceStatusUpdateForm,
    PurchasePricesResponse,
    LaboratoryServicePurchasePriceCreationParams,
    LaboratoryServicePurchasePriceUpdateParams,
    PrintPriceListParams,
    PriceUpdateForm,
    PriceCreateForm,
} from 'chaika-frontend-types/frontend-cms';

import { Price, PriceCreationParams, PriceUpdateParams } from '@store/models/Price/Price';
import { DateFormat, DateHelper } from '@utils/DateHelper';

import { axios } from '@lib/axios';

const BASE_URL = '/api/price';

export class PriceApi {
    public static async getPrices({ query, ...params }: PriceQuery): Promise<PricesResponse> {
        let serviceId = params.serviceId;

        if (Array.isArray(serviceId)) {
            serviceId = serviceId.join(',');
        }

        const res = await axios.get(BASE_URL, {
            params: {
                query: query?.trim(),
                ...params,
                serviceId,
            },
        });

        return res.data;
    }

    public static async createPurchasePrice(params: LaboratoryServicePurchasePriceCreationParams): Promise<void> {
        await axios.post('/api/purchase-price', { laboratoryPrice: params });
    }

    public static async updatePurchasePrice(
        priceId: string,
        params: LaboratoryServicePurchasePriceUpdateParams,
    ): Promise<void> {
        await axios.put(`/api/purchase-price/${priceId}`, { laboratoryPrice: params });
    }

    public static async getPurchasePrices(params: PurchasePricesQuery): Promise<PurchasePriceData[]> {
        const res = await axios.get<PurchasePricesResponse>('/api/purchase-price', { params });
        return res.data.purchasePrices;
    }

    public static async getPricesActualOn(
        params: ActualOnDatePriceQuery,
        date: Date = new Date(),
    ): Promise<ListOfPricesActualOnDateResponse> {
        const isoDate = DateHelper.format(date, DateFormat.DateWithDashSeparator);
        const res = await axios.get(`${BASE_URL}/actual-on/${isoDate}`, { params });

        return res.data;
    }

    public static async createPrice(params: PriceCreationParams): Promise<void> {
        const price = Price.fromForm(params);
        await axios.post(BASE_URL, { price: price.serialize() });
    }

    public static async createPriceNew(params: PriceCreateForm): Promise<void> {
        await axios.post(BASE_URL, { price: params });
    }

    public static async updatePrice(priceId: string, params: PriceUpdateParams): Promise<void> {
        const price = Price.fromForm(params);

        await axios.put(`${BASE_URL}/${priceId}`, { price: price.serialize() });
    }

    public static async updatePriceNew(priceId: string, params: PriceUpdateForm): Promise<void> {
        await axios.put(`${BASE_URL}/${priceId}`, { price: params });
    }

    public static async updatePriceStatus(priceId: string, price: PriceStatusUpdateForm): Promise<void> {
        await axios.put(`${BASE_URL}/${priceId}/status`, { price });
    }

    public static async deletePrice(priceId: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${priceId}`);
    }

    public static async printPriceList(params: PrintPriceListParams): Promise<Blob> {
        const isoDate = DateHelper.format(params.actualOnDate, DateFormat.DateWithDashSeparator);
        const res = await axios.get(`${BASE_URL}/print-price-list`, {
            params: { ...params, actualOnDate: isoDate },
            responseType: 'blob',
            timeout: 0,
        });

        return res.data;
    }

    public static async getCompositeServices(params: PriceQuery = {}): Promise<PricesResponse> {
        const res = await axios.get<PricesResponse>(BASE_URL, {
            params: {
                ...params,
                limit: 500,
                isComposite: true,
            },
        });

        return res.data;
    }
}

export { BASE_URL as PRICE_BASE_URL };
